import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiX } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import Overview from './Overview';
import DeliveryReport from './DeliveryReport';
import Recipients from './Recipients';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Typecast icon components for TypeScript compatibility
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Utility function to determine status badge color based on campaign status
const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-600';
    case 'DRAFT':
      return 'bg-red-100 text-red-600';
    case 'SCHEDULED':
      return 'bg-yellow-100 text-yellow-600';
    case 'COMPLETED':
    case 'SENT':
      return 'bg-blue-100 text-blue-600';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// Displays detailed information and analytics for a specific campaign
// Includes tabs for Overview, Delivery Report, and Recipients
const ViewCampaign: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const campaign = state;

  // State to track the active tab (Overview, Delivery Report, or Recipients)
  const [activeTab, setActiveTab] = useState<
    'Overview' | 'Delivery Report' | 'Recipients'
  >('Overview');

  // State for API response
  const [campaignContext, setCampaignContext] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // ⭐ Preview Modal States
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');

  // ⭐ Merge Tags Modal States
  const [isMergeTagsModalOpen, setIsMergeTagsModalOpen] = useState(false);
  const [mergeTagsValues, setMergeTagsValues] = useState<Record<string, string>>({});
  // const [templateContent, setTemplateContent] = useState<string>('');

  // ⭐ Fetch campaign context when component mounts
  useEffect(() => {
    const fetchCampaignContext = async () => {
      if (!id) {
        return;
      }

      try {
        setLoading(true);
        const { token, tenantId, userId } = getAuthContext();

        if (!token || !tenantId) {
          toast.error('Authentication required');
          setLoading(false);
          return;
        }



        const response = await fetch(
          `${BASE_URL}/v1/campaign-wizard/${id}/context?`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // ⭐ Console log the response

        setCampaignContext(data);
      } catch (error: any) {
        // console.error('❌ Failed to fetch campaign context:', error);
        toast.error(error.message || 'Failed to load campaign details');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignContext();
  }, [id]);

  // ⭐ Extract merge tags from HTML content
  const extractMergeTags = (html: string): string[] => {
    const regex = /{{([^}]+)}}/g;
    const matches = html.match(regex);
    if (!matches) return [];

    // Extract unique tag names without {{ }}
    const uniqueTags = new Set<string>();
    matches.forEach(match => {
      const tagName = match.replace(/{{|}}/g, '').trim();
      uniqueTags.add(tagName);
    });

    return Array.from(uniqueTags);
  };

  // ⭐ Check if all merge tag values are filled
  const areAllMergeTagsFilled = (): boolean => {
    const keys = Object.keys(mergeTagsValues);
    if (keys.length === 0) return false;
    return keys.every(key => mergeTagsValues[key] && mergeTagsValues[key].trim() !== '');
  };

  // ⭐ Handle Preview Email - Check for merge tags first
  const handlePreviewEmail = async () => {
    if (!id) {
      toast.error('Campaign ID not found');
      return;
    }

    try {
      setPreviewLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setPreviewLoading(false);
        return;
      }

      // First, get the campaign context to find the template ID
      const contextResponse = await fetch(
        `${BASE_URL}/v1/campaign-wizard/${id}/context?`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!contextResponse.ok) {
        throw new Error(`HTTP ${contextResponse.status}`);
      }

      const contextData = await contextResponse.json();
      const templateId = contextData?.campaign?.templateId;

      if (!templateId) {
        throw new Error('Template ID not found');
      }

      // Fetch the template to get its HTML
      const templateResponse = await fetch(
        `${BASE_URL}/v1/templates/user-define/${templateId}`,
        {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // ⭐ Handle error response - extract message from API
      if (!templateResponse.ok) {
        let errorMessage = `HTTP ${templateResponse.status}`;
        try {
          const errorData = await templateResponse.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If response body is not JSON, fall back to status text
          errorMessage = templateResponse.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const templateData = await templateResponse.json();

      // ⭐ Extract HTML from blocks array
      let htmlContent = '';
      if (templateData.html) {
        htmlContent = templateData.html;
      } else if (templateData.blocks && Array.isArray(templateData.blocks)) {
        const rawHtmlBlocks = templateData.blocks.filter(
          (block: any) => block.type === 'rawHtml'
        );
        htmlContent = rawHtmlBlocks.map((block: any) => block.text).join('\n');
      }


      // Extract merge tags from HTML
      const tags = extractMergeTags(htmlContent);


      if (tags.length > 0) {
        // ⭐ Set initial values as empty strings
        const initialValues: Record<string, string> = {};
        tags.forEach(tag => {
          initialValues[tag] = '';
        });
        setMergeTagsValues(initialValues);
        setIsMergeTagsModalOpen(true);
        setPreviewLoading(false);
        return;
      }

      // If no merge tags, proceed directly to preview
      await fetchPreview();
    } catch (error: any) {
      // console.error('❌ Failed to check merge tags:', error);
      toast.error(error.message || 'Failed to load template');
      setPreviewLoading(false);
    }
  };

  // ⭐ Fetch preview with merge tag values
  const fetchPreview = async (mergeValues?: Record<string, string>) => {
    if (!id) {
      toast.error('Campaign ID not found');
      return;
    }

    try {
      setPreviewLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setPreviewLoading(false);
        return;
      }


      // ⭐ Build sampleContext with only the provided values
      const requestBody = {
        sampleContext: mergeValues || {}
      };


      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard/${id}/preview?strict=true`,
        {
          method: 'POST',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setPreviewHtml(data.html || '');
      setIsPreviewModalOpen(true);
      setViewMode('desktop');
    } catch (error: any) {
      // console.error('❌ Failed to fetch preview:', error);
      toast.error(error.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // ⭐ Handle OK on merge tags modal
  const handleMergeTagsOk = () => {
    setIsMergeTagsModalOpen(false);
    fetchPreview(mergeTagsValues);
  };

  // ⭐ Handle Cancel on merge tags modal - Call preview API without merge values
  const handleMergeTagsCancel = async () => {
    // Close the merge tags modal
    setIsMergeTagsModalOpen(false);

    try {
      setPreviewLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        setPreviewLoading(false);
        return;
      }


      // ⭐ Call preview API with empty context (strict=false to allow missing merge tags)
      const requestBody = {
        sampleContext: {}
      };


      const response = await fetch(
        `${BASE_URL}/v1/campaign-wizard/${id}/preview?strict=false`,
        {
          method: 'POST',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setPreviewHtml(data.html || '');
      setIsPreviewModalOpen(true);
      setViewMode('desktop');
    } catch (error: any) {
      // console.error('❌ Failed to fetch preview:', error);
      toast.error(error.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Sample data for the Delivery Report tab
  const deliveryData = [
    {
      id: 1,
      recipient: 'Customer A',
      email: 'a@email.com',
      sent: '15-08-2025',
      status: 'Delivered',
    },
    {
      id: 2,
      recipient: 'Customer B',
      email: 'b@email.com',
      sent: '15-08-2025',
      status: 'Not Delivered',
    },
    {
      id: 3,
      recipient: 'Customer C',
      email: 'c@email.com',
      sent: '24-08-2025',
      status: 'Bounced',
    },
    {
      id: 4,
      recipient: 'Customer D',
      email: 'd@email.com',
      sent: '15-08-2025',
      status: 'Delivered',
    },
    {
      id: 5,
      recipient: 'Customer E',
      email: 'e@email.com',
      sent: '15-08-2025',
      status: 'Not Delivered',
    },
    {
      id: 6,
      recipient: 'Customer F',
      email: 'f@email.com',
      sent: '24-08-2025',
      status: 'Bounced',
    },
    {
      id: 7,
      recipient: 'Customer G',
      email: 'g@email.com',
      sent: '24-08-2025',
      status: 'Bounced',
    },
  ];

  const recipientData = [
    {
      id: 1,
      name: 'John Doe',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Political'],
      lastOpened: '15-08-2025',
      lastClicked: '15-08-2025',
    },
    {
      id: 2,
      name: 'Brad Pitt',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Sports'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
    {
      id: 3,
      name: 'Robert Pattinson',
      email: 'example@email.com',
      segment: 'Old User',
      tags: ['Entertainment'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
    {
      id: 4,
      name: 'Tom Cruise',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Political'],
      lastOpened: '15-08-2025',
      lastClicked: '15-08-2025',
    },
    {
      id: 5,
      name: 'Bruce Willis',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Sports'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
    {
      id: 6,
      name: 'Mel Gibson',
      email: 'example@email.com',
      segment: 'Old User',
      tags: ['Entertainment'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
    {
      id: 7,
      name: 'Arnold Schwarzenegar',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Political'],
      lastOpened: '15-08-2025',
      lastClicked: '15-08-2025',
    },
    {
      id: 8,
      name: 'Sylvestor Stallone',
      email: 'example@email.com',
      segment: 'New User',
      tags: ['Sports'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
    {
      id: 9,
      name: 'Jackie Chan',
      email: 'example@email.com',
      segment: 'Old User',
      tags: ['Entertainment'],
      lastOpened: '24-08-2025',
      lastClicked: '24-08-2025',
    },
  ];

  // ⭐ Get data from campaign (passed from table) with fallbacks
  const getCampaignTitle = () => {
    if (campaignContext?.campaign?.title) {
      return campaignContext.campaign.title;
    }
    return campaign?.title || campaign?.name || 'Untitled Campaign';
  };

  const getCampaignSubject = () => {
    if (campaignContext?.campaign?.subject) {
      return campaignContext.campaign.subject;
    }
    return campaign?.subject || '';
  };

  const getCampaignStatus = () => {
    if (campaignContext?.campaign?.status) {
      return campaignContext.campaign.status;
    }
    return campaign?.status || 'DRAFT';
  };

  // ⭐ Get segments from API response
  const getSegmentNames = () => {
    const segmentIds = campaignContext?.campaign?.segmentIds;

    if (!segmentIds || !Array.isArray(segmentIds) || segmentIds.length === 0) {
      return 'N/A';
    }

    const validSegmentIds = segmentIds.filter(id => id && id.trim() !== '');

    if (validSegmentIds.length === 0) {
      return 'N/A';
    }

    const segments = campaignContext?.segments || [];
    const matchingSegments = validSegmentIds.map(id => {
      const found = segments.find((seg: any) => seg.id === id);
      return found?.name || id;
    });

    return matchingSegments.join(', ');
  };

  // ⭐ Get tags from API response
  const getTagNames = () => {
    const tagIds = campaignContext?.campaign?.tagIds;

    if (!tagIds || !Array.isArray(tagIds) || tagIds.length === 0) {
      return 'N/A';
    }

    const validTagIds = tagIds.filter(id => id && id.trim() !== '');

    if (validTagIds.length === 0) {
      return 'N/A';
    }

    const tags = campaignContext?.tags || [];
    const matchingTags = validTagIds.map(id => {
      if (tags.includes(id)) {
        return id;
      }
      return id;
    });

    return matchingTags.join(', ');
  };

  // ⭐ Get delivered date
  const getDeliveredDate = () => {
    if (campaignContext?.campaign?.scheduledAt) {
      const date = new Date(campaignContext.campaign.scheduledAt);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    if (campaign?.scheduledAt) {
      const date = new Date(campaign.scheduledAt);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    if (campaign?.updatedAt) {
      const date = new Date(campaign.updatedAt);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    return 'N/A';
  };

  // ⭐ Get metrics from the campaign data (passed from table)
  const getCampaignMetrics = () => {
    if (campaign?.matrics) {
      return campaign.matrics;
    }
    return {
      sent: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      openRate: 0,
      clickRate: 0,
      bounced: 0,
      unsubscribed: 0,
    };
  };

  const title = getCampaignTitle();
  const subject = getCampaignSubject();
  const status = getCampaignStatus();
  const segmentDisplay = getSegmentNames();
  const tagDisplay = getTagNames();
  const deliveredDate = getDeliveredDate();
  const metrics = getCampaignMetrics();

  // ⭐ Check if all merge tag values are filled for enabling OK button
  const isOkEnabled = areAllMergeTagsFilled();

  return (
    <>
      <div className='p-2'>
        {/* Back Button */}
        <button
          onClick={() => navigate('/campaigns')}
          className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
          style={{ color: '#00899E', fontSize: '14px' }}
        >
          <FiChevronLeftComponent className='mr-1' /> Back to Campaigns
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 className='text-[20px] font-bold text-[#3F5265]'>
              View Campaign
            </h2>
            <p className='text-[#657584] mb-6 text-[16px]'>
              Campaign performance and analytics
            </p>
          </div>
        </div>

        {/* ⭐ Show loading indicator */}
        {loading && (
          <div className='flex justify-center items-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500'></div>
          </div>
        )}

        {/* Campaign Header */}
        <div className='bg-white p-4 rounded-lg mb-6 border border-gray-200 shadow-sm'>
          <div className='flex justify-between items-start'>
            <div className='flex items-center gap-3'>
              <h2 className='text-xl font-semibold text-[#1F2937] text-[18px]'>
                {title}
              </h2>
              {/* ⭐ Status Badge */}
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(status)}`}
              >
                {status ? status.charAt(0) + status.slice(1).toLowerCase() : 'Unknown'}
              </span>
            </div>

            {/* ⭐ Preview Email Button */}
            <button
              onClick={handlePreviewEmail}
              disabled={previewLoading}
              className={`text-[#FF7A59] px-3 py-2 rounded hover:bg-orange-50 text-sm border border-[#FF7A59] text-[14px] ${previewLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {previewLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z" />
                  </svg>
                  Loading...
                </span>
              ) : (
                'Preview Email'
              )}
            </button>
          </div>

          <p className='text-gray-500 mt-1'>{subject}</p>

          <div className='flex flex-wrap gap-2 mt-5 text-sm text-gray-600 bg-[#F5F8FA] p-3 rounded justify-between'>
            <span className='text-[#9FA9B2] text-[12px]'>
              SEGMENT{' '}
              <span className='text-[#263445] text-[16px] ml-2'>
                {segmentDisplay}
              </span>
            </span>
            <span className='text-[#9FA9B2] text-[12px]'>
              TAGS{' '}
              <span className='text-[#263445] text-[16px] ml-2'>
                {tagDisplay}
              </span>
            </span>
            <span className='text-[#9FA9B2] text-[12px]'>
              DELIVERED{' '}
              <span className='text-[#263445] text-[16px] ml-2'>
                {deliveredDate}
              </span>
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className='bg-white rounded-md border border-gray-200 shadow-sm'>
          <div className='flex space-x-6 px-6 pt-4'>
            {['Overview'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as typeof activeTab)}
                className={`pb-2 font-medium text-[14px] ${activeTab === tab
                  ? 'border-b-2 border-[#00899E] text-[#00899E]'
                  : 'text-gray-500'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className={activeTab === 'Overview' ? 'p-6' : ''}>
            {activeTab === 'Overview' && (
              <Overview
                campaign={campaignContext?.campaign || campaign}
                metrics={metrics}
              />
            )}
            {activeTab === 'Delivery Report' && (
              <DeliveryReport deliveryData={deliveryData} />
            )}
            {activeTab === 'Recipients' && (
              <Recipients recipients={recipientData} />
            )}
          </div>
        </div>
      </div>

      {/* ⭐ Merge Tags Modal */}
      {isMergeTagsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-[#3F5265]">Merge Tags</h3>
              <button
                onClick={handleMergeTagsCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXComponent size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                Enter values for the merge tags found in this email:
              </p>

              <div className="space-y-3">
                {Object.keys(mergeTagsValues).map((tag) => (
                  <div key={tag} className="flex items-center gap-3">
                    <label className="w-1/3 text-sm font-medium text-gray-700">
                      {tag}
                    </label>
                    <input
                      type="text"
                      value={mergeTagsValues[tag] || ''}
                      onChange={(e) => {
                        setMergeTagsValues(prev => ({
                          ...prev,
                          [tag]: e.target.value
                        }));
                      }}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00899E] ${mergeTagsValues[tag] && mergeTagsValues[tag].trim() !== ''
                        ? 'border-green-500'
                        : 'border-gray-300'
                        }`}
                      placeholder={`Enter ${tag}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={handleMergeTagsCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMergeTagsOk}
                disabled={!isOkEnabled}
                className={`px-4 py-2 rounded-md transition-colors ${isOkEnabled
                  ? 'bg-[#00899E] text-white hover:bg-[#007A8A]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⭐ Preview Modal */}
      {isPreviewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-[#3F5265]">Preview Email</h3>
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXComponent size={24} />
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex justify-center items-center p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center bg-[#E8F4F6] rounded-lg p-1 gap-1">
                {/* Desktop */}
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-2.5 rounded-md transition-colors ${viewMode === 'desktop'
                    ? 'bg-white text-[#00899E] shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                  title="Desktop View"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </button>

                {/* Mobile */}
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-2.5 rounded-md transition-colors ${viewMode === 'mobile'
                    ? 'bg-white text-[#00899E] shadow-sm'
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                  title="Mobile View"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body - Template Preview */}
            <div className="flex-1 p-4 overflow-auto bg-gray-100">
              {viewMode === 'desktop' ? (
                // ⭐ Desktop View - Full width
                <div className="w-full max-w-3xl mx-auto">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {previewHtml ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                        className="w-full"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-400">
                        No preview available
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // ⭐ Mobile View - Simple centered container without phone frame
                <div className="flex justify-center">
                  <div
                    className="w-[375px] bg-white rounded-lg shadow-lg overflow-hidden"
                    style={{ height: 'fit-content' }}
                  >
                    {previewHtml ? (
                      <div
                        style={{
                          transform: 'scale(0.72)',
                          transformOrigin: 'top center',
                          width: '138.888%',
                          marginLeft: '-19.444%',
                          marginBottom: `calc(-28% )`,
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                          className="w-full"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-400">
                        No preview available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </>
  );
};

export default ViewCampaign;